import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router'
import { SignUp, useAuth, useClerk } from '@clerk/clerk-react'
import { ArrowUpRight, LogOut } from 'lucide-react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useAuthSync } from '@/hooks/use-auth-sync'
import { useHttpClientSetup } from '@/hooks/use-http-client-setup'
import { postToOgsmSsoBridge } from '@/lib/ogsm-sync-url'
import {
  useUserStore,
  selectAllowedApps,
  selectHasCompassAccess,
  selectRole,
  selectIsProfileReady,
} from '@/stores/user.store'
import { Typography } from '@/components/ui/typography'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import logoImg from '@/assets/images/greybull-logo-full.svg'

type PortalSearchParams = {
  __clerk_ticket?: string
  __clerk_status?: string
}

export const Route = createFileRoute('/portal')({
  component: PortalLayout,
  validateSearch: (search: Record<string, unknown>): PortalSearchParams => ({
    __clerk_ticket:
      typeof search.__clerk_ticket === 'string'
        ? search.__clerk_ticket
        : undefined,
    __clerk_status:
      typeof search.__clerk_status === 'string'
        ? search.__clerk_status
        : undefined,
  }),
})

function PortalLayout() {
  const { isSignedIn, isLoaded } = useAuth()
  const { __clerk_ticket } = Route.useSearch()
  const isHttpReady = useHttpClientSetup()
  const { isLoading: isAuthLoading, error: authError } = useAuthSync()
  const hasCompassAccess = useUserStore(selectHasCompassAccess)
  const role = useUserStore(selectRole)
  const isProfileReady = useUserStore(selectIsProfileReady)

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <img src={logoImg} alt="Logo" className="h-25 w-auto" />
      </div>
    )
  }

  // Invitation flow: show sign-up form when user arrives with a Clerk ticket
  if (!isSignedIn && __clerk_ticket) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background">
        <img src={logoImg} alt="Logo" className="h-12 w-auto" />
        <SignUp
          appearance={{
            elements: {
              rootBox: 'mx-auto',
              cardBox: '!shadow-none !border border-border',
              formButtonPrimary: '!bg-primary !border-primary !shadow-none',
            },
          }}
          routing="hash"
          forceRedirectUrl="/portal"
          fallbackRedirectUrl="/portal"
        />
      </div>
    )
  }

  if (!isSignedIn) {
    return <Navigate to="/" />
  }

  if (!isHttpReady || isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <img src={logoImg} alt="Logo" className="h-25 w-auto animate-pulse" />
      </div>
    )
  }

  if (authError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <img src={logoImg} alt="Logo" className="h-25 w-auto" />
        <Typography as="p" className="text-destructive">
          Failed to load user profile
        </Typography>
        <Typography variant="muted">{authError}</Typography>
        <Button
          variant="link"
          size="sm"
          onClick={() => window.location.reload()}
        >
          Try again
        </Button>
      </div>
    )
  }

  if (isProfileReady && !hasCompassAccess) {
    return <Navigate to="/auth/redirect" />
  }

  // Redirect internal users to the main app
  if (isProfileReady && role && !role.startsWith('PORTCO_')) {
    return <Navigate to="/companies" />
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-screen flex-col bg-background">
        <a
          href="#main-content"
          className="fixed left-4 top-4 z-[100] -translate-y-24 rounded-md bg-background px-4 py-2 text-sm font-medium text-primary shadow-md transition-transform focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Skip to content
        </a>
        <PortalHeader />
        <Separator className="bg-primary/10" />
        <main id="main-content" tabIndex={-1} className="min-h-0 flex-1">
          <Outlet />
        </main>
      </div>
    </TooltipProvider>
  )
}

function PortalHeader() {
  const clerk = useClerk()
  const { getToken } = useAuth()
  const clerkUser = useUserStore((s) => s.clerkUser)
  const allowedApps = useUserStore(selectAllowedApps)
  const ogsmBaseUrl = import.meta.env.VITE_OGSM_URL?.trim() || ''
  const canAccessOgsm = allowedApps.includes('ogsm') && Boolean(ogsmBaseUrl)

  const handleOpenOgsm = () => {
    void postToOgsmSsoBridge({
      getToken,
      ogsmBaseUrl,
      openInNewTab: true,
    }).catch((err) => {
      console.error('OGSM SSO bridge failed', err)
    })
  }

  return (
    <header className="flex h-14 items-center justify-between px-6">
      <img src={logoImg} alt="Compass" className="h-7 w-auto" />
      <div className="flex items-center gap-3">
        {canAccessOgsm && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs text-primary/70 hover:text-primary"
            onClick={handleOpenOgsm}
          >
            Legacy OGSM
            <ArrowUpRight className="size-3.5" />
          </Button>
        )}
        {clerkUser && (
          <Typography variant="body-sm" className="text-primary/70">
            {clerkUser.firstName} {clerkUser.lastName}
          </Typography>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs text-primary/70 hover:text-primary"
          onClick={() => clerk.signOut({ redirectUrl: '/' })}
        >
          <LogOut className="size-3.5" />
          Sign out
        </Button>
      </div>
    </header>
  )
}
